import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Clock, Tag, CheckCircle2, ShieldAlert } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, LabelList } from "recharts";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/QuestionCard";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function QuestionDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => { api.get(`/questions/${id}`).then(({ data }) => setQ(data)); }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!q) return <div className="max-w-3xl mx-auto px-4 py-12 text-muted-foreground">{t("common.loading")}</div>;

  const showResults = q.status === "closed" || q.user_voted;
  const canVote = q.status === "active" && !q.user_voted;
  const chartData = q.options.map((o, i) => ({ name: o, value: q.results[i], idx: i }));

  const vote = async () => {
    if (!user) { nav("/login"); return; }
    if (user.verification_status !== "verified") { toast.error(t("q.mustVerify")); return; }
    if (selected === null) { toast.error(t("q.select")); return; }
    setSubmitting(true);
    try {
      await api.post(`/questions/${id}/vote`, { option_index: Number(selected) });
      toast.success(t("q.voteSuccess"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <Link to="/" data-testid="back-home" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" />{t("common.back")}</Link>

      <div className="flex items-center gap-2 mb-3">
        <StatusBadge status={q.status} />
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Tag className="h-3 w-3" />{q.category}</span>
      </div>
      <h1 data-testid="question-title" className="font-heading text-3xl font-black tracking-tighter">{q.title}</h1>
      <p className="mt-4 text-muted-foreground leading-relaxed">{q.description}</p>

      <div className="flex flex-wrap gap-4 mt-4 text-sm">
        {q.source_url ? <a href={q.source_url} target="_blank" rel="noreferrer" data-testid="source-link" className="inline-flex items-center gap-1 text-primary hover:underline"><ExternalLink className="h-4 w-4" />{t("q.source")}</a> : null}
        <span className="inline-flex items-center gap-1 text-muted-foreground"><Clock className="h-4 w-4" />{t("q.closes")}: {new Date(q.closes_at).toLocaleString()}</span>
      </div>

      <Card className="mt-8 p-6">
        {canVote ? (
          <>
            {!user && <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground bg-secondary rounded-md p-3"><ShieldAlert className="h-4 w-4" />{t("q.mustLogin")}</div>}
            {user && user.verification_status !== "verified" && <div data-testid="verify-warning" className="mb-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3"><ShieldAlert className="h-4 w-4" />{t("q.mustVerify")}</div>}
            <h3 className="font-heading font-bold text-lg mb-4">{t("q.select")}</h3>
            <RadioGroup value={selected} onValueChange={setSelected} className="space-y-3">
              {q.options.map((o, i) => (
                <label key={i} data-testid={`vote-option-${i}`} htmlFor={`opt-${i}`} className="flex items-center gap-3 rounded-md border border-border p-4 cursor-pointer hover:border-primary hover:bg-secondary transition-colors">
                  <RadioGroupItem value={String(i)} id={`opt-${i}`} />
                  <span className="font-medium">{o}</span>
                </label>
              ))}
            </RadioGroup>
            <Button data-testid="submit-vote" onClick={vote} disabled={submitting} className="mt-5 w-full">{submitting ? t("common.loading") : t("q.confirm")}</Button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-primary" />{t("q.results")}</h3>
              {q.user_voted && <span data-testid="voted-badge" className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">{t("q.voted")}</span>}
            </div>
            <div style={{ width: "100%", height: Math.max(180, q.options.length * 64) }}>
              <ResponsiveContainer>
                <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 40 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} data-testid="results-bar">
                    {chartData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    <LabelList dataKey="value" position="right" className="fill-foreground text-sm font-semibold" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-sm text-muted-foreground font-mono">{t("q.total")}: {q.total_votes}</div>
          </>
        )}
      </Card>
    </div>
  );
}
